import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type { TeamLLMDTO } from "@shaarp/common";
import { Star } from "lucide-react";
import { LLMModelCardDescription } from "./llm-model-card-description";
import { LLMModelCardHeader } from "./llm-model-card-header";

interface LLMModelCardProps {
  llm: TeamLLMDTO;
  providerId: string;
  onUpdateAvailability: (llm: TeamLLMDTO, available: boolean) => void;
  onSetAsDefault: (llm: TeamLLMDTO) => void;
}

export function LLMModelCard({
  llm,
  providerId,
  onUpdateAvailability,
  onSetAsDefault,
}: LLMModelCardProps) {
  const { t } = useTranslation();

  const handleSetAsDefault = () => {
    onSetAsDefault(llm);
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col justify-between p-0 rounded-xl border shadow-xs min-h-[220px] text-left transition-all duration-200",
        llm.available
          ? "bg-card border-border hover:shadow-md"
          : "bg-muted/50 text-muted-foreground border-muted-foreground/20",
        llm.default && "ring-2 ring-primary/20 border-primary/30 shadow-md",
        llm.adminOnly && "border-warning border-2"
      )}
    >
      {/* Header */}
      <LLMModelCardHeader
        llm={llm}
        providerId={providerId}
        onUpdateAvailability={onUpdateAvailability}
      />

      {/* Content */}
      <div className="px-6 pb-3">
        <LLMModelCardDescription modelId={llm.id} />
      </div>

      {/* Footer */}
      <div className="flex justify-center px-6 pb-6 mt-auto">
        {llm.available && llm.default ? (
          <Badge
            variant="default"
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium"
          >
            <Star className="w-3 h-3" />
            {t("team.components.llmPermissions.default")}
          </Badge>
        ) : (
          llm.available && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center justify-center w-full gap-2 text-sm transition-all duration-200 hover:bg-accent hover:text-accent-foreground"
                    onClick={handleSetAsDefault}
                  >
                    <Star className="w-4 h-4" />
                    {t("team.components.llmPermissions.setDefault")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">
                    {t("team.components.llmPermissions.tooltip.makeDefault")}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        )}
      </div>
    </div>
  );
}
